import React, { useState, useEffect, useContext } from "react";
import qs from "query-string";
import * as Yup from "yup";
import { useHistory, useLocation, useParams, Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import { Typography, Popover } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Helmet } from "react-helmet";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  Building2,
  UserPlus,
} from "lucide-react";

import ColorModeContext from "../../layout/themeContext";
import ThemeToggle from "../../components/ThemeToggle";
import { BACKEND_URL } from "../../config/env";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSettings from "../../hooks/useSettings";

const CheckIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M5 12L10 17L20 7" />
  </svg>
);

const SIGNUP_LANGUAGE_PATHS = {
  pt: "/pt/registre-se",
  en: "/en/sign-up",
  es: "/es/registrarse",
  tr: "/tr/kayit-ol",
  ar: "/ar/register",
};

const SIGNUP_PATH_LANGUAGES = {
  "/pt/registre-se": "pt",
  "/cadastre-se": "pt",
  "/en/sign-up": "en",
  "/sign-up": "en",
  "/es/registrarse": "es",
  "/registrarse": "es",
  "/tr/kayit-ol": "tr",
  "/kayit-ol": "tr",
  "/ar/register": "ar",
};

const WHATSAPP_PHONE = "5581989974426";

const WHATSAPP_TEXTS = {
  pt: { label: "Dúvidas?", message: "Olá! Quero saber mais sobre o ChatIA." },
  en: { label: "Questions?", message: "Hi. I want to know more about ChatIA." },
  es: { label: "¿Dudas?", message: "Hola. Quiero saber más sobre ChatIA." },
  tr: { label: "Sorular?", message: "Merhaba. ChatIA hakkında daha fazla bilgi almak istiyorum." },
  ar: { label: "أسئلة؟", message: "مرحباً. أريد معرفة المزيد عن ChatIA." },
};

const normalizeSignupLanguage = (lang) => {
  if (!lang) return null;
  const normalized = String(lang).toLowerCase().split("-")[0];
  if (normalized === "br") return "pt";
  return ["pt", "en", "es", "tr", "ar"].includes(normalized) ? normalized : null;
};

const getSignupLanguageFromPath = (pathname, langParam) => {
  const fromParam = normalizeSignupLanguage(langParam);
  if (fromParam) return fromParam;
  return SIGNUP_PATH_LANGUAGES[(pathname || "").toLowerCase()] || null;
};

const getConfigurableWhatsAppUrl = (phone, message) =>
  `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

const normalizeWhatsAppPhone = (phone) =>
  String(phone || "").replace(/\D/g, "");

const SIGNUP_PHONE_DEFAULT_COUNTRY = {
  pt: "br",
  en: "us",
  es: "es",
  tr: "tr",
  ar: "sa",
};

const SIGNUP_PHONE_SEARCH_PLACEHOLDER = {
  pt: "Buscar país",
  en: "Search country",
  es: "Buscar país",
  tr: "Ülke ara",
  ar: "ابحث عن الدولة",
};

const getSignupPhoneCountry = (language) => {
  const shortCode = normalizeSignupLanguage(language) || "pt";
  return SIGNUP_PHONE_DEFAULT_COUNTRY[shortCode] || "br";
};

const WhatsAppIcon = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    {...props}
  >
    <path d="M20.52 3.48A11.74 11.74 0 0 0 12.06 0C5.44 0 .06 5.37.06 11.99c0 2.11.55 4.17 1.6 5.99L0 24l6.2-1.63a11.98 11.98 0 0 0 5.86 1.49h.01c6.62 0 11.99-5.37 11.99-11.99 0-3.2-1.25-6.2-3.54-8.39Zm-8.46 18.4h-.01a9.92 9.92 0 0 1-5.06-1.39l-.36-.22-3.68.97.98-3.58-.24-.37a9.95 9.95 0 0 1-1.52-5.28C2.17 6.1 6.49 1.79 11.99 1.79c2.64 0 5.12 1.03 6.77 2.9a9.5 9.5 0 0 1 2.57 6.6c0 5.5-4.32 10.59-9.27 10.59Zm5.39-7.35c-.29-.15-1.74-.85-2.01-.95-.27-.1-.47-.15-.67.15-.2.29-.77.95-.95 1.14-.18.2-.35.22-.64.07-.29-.15-1.22-.45-2.32-1.43-.86-.77-1.43-1.72-1.6-2-.17-.29-.02-.44.13-.58.13-.13.29-.35.44-.53.15-.18.2-.29.29-.48.1-.2.05-.37-.02-.53-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.53.07-.81.37-.29.29-1.09 1.06-1.09 2.57 0 1.51 1.11 2.96 1.26 3.16.15.2 2.17 3.31 5.25 4.64.73.31 1.3.5 1.75.64.74.24 1.41.2 1.94.12.59-.09 1.74-.71 1.99-1.39.24-.67.24-1.24.17-1.39-.07-.15-.27-.22-.57-.37Z" />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  "@keyframes loginSpin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes loginFadeIn": {
    from: { opacity: 0, transform: "translateY(16px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  root: {
    display: "flex",
    width: "100vw",
    minHeight: "100vh",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    padding: "20px",
    backgroundColor: theme.mode === "light" ? "#f5f5f5" : "#0a0a0a",
    transition: "background-color 0.3s ease",
  },

  topRightControls: {
    position: "absolute",
    top: 16,
    right: 20,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  langTriggerButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 8,
    borderRadius: 6,
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.15)"
        : "rgba(0,0,0,0.06)",
    },
  },
  langTriggerFlag: {
    width: 28,
    height: 20,
    objectFit: "cover",
    borderRadius: 3,
    border: theme.mode === "dark"
      ? "1.5px solid rgba(255,255,255,0.3)"
      : "1.5px solid rgba(0,0,0,0.15)",
    display: "block",
  },

  langPopoverPaper: {
    borderRadius: 14,
    minWidth: 230,
    overflow: "hidden",
    boxShadow: "0 10px 36px rgba(0,0,0,0.18)",
    border: "none",
    background: theme.mode === "dark" ? "#121212" : "#ffffff",
  },
  langHeader: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#ffffff",
  },
  langHeaderTitle: {
    fontWeight: 600,
    fontSize: 13,
    color: "inherit",
  },
  langOptionsList: {
    padding: 6,
  },
  langOptionItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      background: theme.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)",
    },
  },
  langOptionItemSelected: {
    background: "rgba(6, 207, 156, 0.10)",
    "&:hover": { background: "rgba(6, 207, 156, 0.15)" },
  },
  langFlagImg: {
    width: 30,
    height: 20,
    objectFit: "cover",
    borderRadius: 3,
    flexShrink: 0,
    border: theme.mode === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(0,0,0,0.1)",
  },
  langOptionName: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.mode === "dark" ? "#ffffff" : "#1a1a1a",
    flex: 1,
  },
  langCheckWrapper: {
    width: 18,
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    flexShrink: 0,
  },

  cardWrap: {
    position: "relative",
    width: "100%",
    maxWidth: 460,
    animation: "$loginFadeIn 0.6s ease-out both",
  },
  blurLayer: {
    position: "absolute",
    inset: 0,
    borderRadius: 24,
    overflow: "hidden",
    pointerEvents: "none",
  },
  blurOrb1: {
    position: "absolute",
    top: -40, left: -40, right: -40, bottom: -40,
    borderRadius: "9999px",
    background: theme.mode === "light"
      ? "linear-gradient(to right, transparent, rgba(107,114,128,0.18), transparent)"
      : "linear-gradient(to right, transparent, rgba(156,163,175,0.20), transparent)",
    filter: "blur(20px)",
    opacity: theme.mode === "light" ? 0.4 : 0.5,
    animation: "$loginSpin 10s linear infinite",
  },
  blurOrb2: {
    position: "absolute",
    top: -80, left: -80, right: -80, bottom: -80,
    borderRadius: "9999px",
    background: theme.mode === "light"
      ? "linear-gradient(to right, transparent, rgba(75,85,99,0.12), transparent)"
      : "linear-gradient(to right, transparent, rgba(107,114,128,0.15), transparent)",
    filter: "blur(40px)",
    opacity: theme.mode === "light" ? 0.25 : 0.3,
    animation: "$loginSpin 18s linear infinite reverse",
  },
  borderGradient: {
    position: "absolute",
    inset: 0,
    borderRadius: 24,
    padding: 1,
    background: theme.mode === "light"
      ? "linear-gradient(to bottom, rgba(209,213,219,0.9), rgba(156,163,175,0.7), rgba(107,114,128,0.5))"
      : "linear-gradient(to bottom, rgba(209,213,219,0.4), rgba(75,85,99,0.6), rgba(31,41,55,0.5))",
    pointerEvents: "none",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
  },
  card: {
    position: "relative",
    overflow: "visible",
    borderRadius: 24,
    boxShadow: theme.mode === "light"
      ? "inset 0 0 0 1px rgba(0,0,0,0.05)"
      : "inset 0 0 0 1px rgba(255,255,255,0.15)",
    backgroundColor: theme.mode === "light"
      ? "hsl(220, 13%, 95%)"
      : "#11141b",
    backgroundImage: theme.mode === "light"
      ? `
        radial-gradient(at 88% 40%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 49% 30%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 14% 26%, hsl(220, 13%, 95%) 0px, transparent 85%),
        radial-gradient(at 0% 64%, hsl(220, 13%, 82%) 0px, transparent 85%),
        radial-gradient(at 41% 94%, hsl(215, 14%, 86%) 0px, transparent 85%),
        radial-gradient(at 100% 99%, hsl(217, 19%, 88%) 0px, transparent 85%)
      `
      : `linear-gradient(135deg, #1f2940 0%, #161a22 45%, #11141b 100%)`,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.mode === "light"
        ? "inset 0 0 0 1px rgba(107,114,128,0.25), 0 10px 40px -10px rgba(107,114,128,0.25)"
        : "inset 0 0 0 1px rgba(156,163,175,0.3), 0 10px 40px -10px rgba(107,114,128,0.4)",
    },
  },
  cardInner: {
    position: "relative",
    padding: 24,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: { padding: 32 },
    [theme.breakpoints.up("md")]: { padding: 40 },
  },

  logoImg: {
    display: "block",
    margin: "0 auto 16px",
    maxWidth: 150,
    height: "auto",
  },

  header: {
    marginBottom: 24,
  },
  secureRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: theme.mode === "light" ? "#4b5563" : "#9ca3af",
    marginBottom: 10,
  },
  secureDot: {
    height: 6,
    width: 6,
    borderRadius: "50%",
    background: theme.mode === "light"
      ? "rgba(16, 185, 129, 0.9)"
      : "rgba(52, 211, 153, 0.85)",
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: theme.mode === "light" ? "#111827" : "#ffffff",
    margin: 0,
    [theme.breakpoints.up("sm")]: { fontSize: 26 },
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  fieldLabel: {
    display: "block",
    fontSize: 11,
    fontWeight: 500,
    color: theme.mode === "light" ? "#374151" : "#d1d5db",
    marginBottom: 6,
  },

  inputWrap: {
    position: "relative",
  },
  inputIconLeft: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 12,
    display: "flex",
    alignItems: "center",
    color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
    pointerEvents: "none",
  },
  inputIconRight: {
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 12,
    display: "flex",
    alignItems: "center",
    color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    padding: 0,
    "&:hover": {
      color: theme.mode === "light" ? "#111827" : "#e5e7eb",
    },
  },
  input: {
    width: "100%",
    borderRadius: 10,
    background: theme.mode === "light"
      ? "rgba(255, 255, 255, 0.85)"
      : "rgba(17, 24, 39, 0.5)",
    color: theme.mode === "light" ? "#111827" : "#f3f4f6",
    boxShadow: theme.mode === "light"
      ? "inset 0 0 0 1px rgba(156, 163, 175, 0.5)"
      : "inset 0 0 0 1px rgba(75, 85, 99, 0.3)",
    border: "none",
    outline: "none",
    padding: "10px 40px",
    fontSize: 13,
    fontFamily: "inherit",
    transition: "box-shadow 0.15s ease",
    "&::placeholder": {
      color: theme.mode === "light" ? "#9ca3af" : "#6b7280",
    },
    "&:focus": {
      boxShadow: theme.mode === "light"
        ? "inset 0 0 0 2px rgba(75, 85, 99, 0.55)"
        : "inset 0 0 0 2px rgba(156, 163, 175, 0.5)",
    },
  },
  inputWithRight: {
    paddingRight: 44,
  },
  inputError: {
    boxShadow: "inset 0 0 0 1.5px rgba(239, 68, 68, 0.7) !important",
  },
  helperError: {
    fontSize: 11,
    color: "#ef4444",
    marginTop: 4,
  },
  phoneInputWrap: {
    position: "relative",
    zIndex: 2,
    "& .react-tel-input": {
      width: "100%",
      fontFamily: "inherit",
    },
    "& .react-tel-input .form-control": {
      width: "100%",
      height: 38,
      borderRadius: 10,
      background: theme.mode === "light"
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(17, 24, 39, 0.5)",
      color: theme.mode === "light" ? "#111827" : "#f3f4f6",
      boxShadow: theme.mode === "light"
        ? "inset 0 0 0 1px rgba(156, 163, 175, 0.5)"
        : "inset 0 0 0 1px rgba(75, 85, 99, 0.3)",
      border: "none",
      outline: "none",
      paddingLeft: "52px",
      fontSize: 13,
      fontFamily: "inherit",
      transition: "box-shadow 0.15s ease",
      "&:focus": {
        boxShadow: theme.mode === "light"
          ? "inset 0 0 0 2px rgba(75, 85, 99, 0.55)"
          : "inset 0 0 0 2px rgba(156, 163, 175, 0.5)",
      },
    },
    "& .react-tel-input .flag-dropdown": {
      border: "none",
      borderRadius: "10px 0 0 10px",
      background: "transparent",
    },
    "& .react-tel-input .flag-dropdown.open": {
      zIndex: 1600,
    },
    "& .react-tel-input .selected-flag": {
      borderRadius: "10px 0 0 10px",
      background: "transparent !important",
      width: 44,
      paddingLeft: 12,
    },
    "& .react-tel-input .country-list": {
      position: "absolute !important",
      top: "auto !important",
      bottom: "calc(100% + 8px) !important",
      left: "50% !important",
      transform: "translateX(-50%) !important",
      width: "min(360px, calc(100vw - 32px)) !important",
      maxWidth: "calc(100vw - 32px) !important",
      maxHeight: "min(320px, calc(100dvh - 24px)) !important",
      margin: "0 !important",
      borderRadius: 12,
      background: theme.mode === "light" ? "#ffffff" : "#111827",
      color: theme.mode === "light" ? "#111827" : "#f3f4f6",
      boxShadow: "0 12px 32px rgba(0,0,0,0.28)",
      border: theme.mode === "light"
        ? "1px solid rgba(0,0,0,0.08)"
        : "1px solid rgba(255,255,255,0.10)",
      overflowX: "hidden",
      overflowY: "auto",
      zIndex: 1400,
      ...theme.scrollbarStyles,
      [theme.breakpoints.down("xs")]: {
        position: "fixed !important",
        bottom: "max(18px, env(safe-area-inset-bottom)) !important",
        left: "50vw !important",
        width: "min(340px, calc(100vw - 24px)) !important",
        maxWidth: "calc(100vw - 24px) !important",
        maxHeight: "min(300px, calc(100dvh - 36px)) !important",
      },
    },
    "& .react-tel-input .country-list .search": {
      background: theme.mode === "light" ? "#ffffff" : "#111827",
      padding: "8px",
    },
    "& .react-tel-input .country-list .search-box": {
      width: "100%",
      borderRadius: 8,
      border: theme.mode === "light"
        ? "1px solid rgba(0,0,0,0.12)"
        : "1px solid rgba(255,255,255,0.14)",
      background: theme.mode === "light" ? "#ffffff" : "#0b1220",
      color: theme.mode === "light" ? "#111827" : "#f3f4f6",
      fontSize: 13,
      padding: "8px 10px",
      margin: 0,
    },
    "& .react-tel-input .country-list .country": {
      position: "relative",
      minHeight: 38,
      padding: "10px 14px 10px 48px",
      "&:hover": {
        background: theme.mode === "light"
          ? "rgba(0,0,0,0.04)"
          : "rgba(255,255,255,0.06)",
      },
      "&.highlight": {
        background: theme.mode === "light"
          ? "rgba(0,0,0,0.06)"
          : "rgba(255,255,255,0.10)",
      },
    },
    "& .react-tel-input .country-list .flag": {
      position: "absolute",
      left: 13,
      top: "50%",
      marginTop: "-10px",
      marginRight: 0,
    },
    "& .react-tel-input .country-list .country-name": {
      display: "inline-block",
      marginRight: 6,
      maxWidth: "220px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: {
        maxWidth: "190px",
      },
    },
    "& .react-tel-input .country-list .dial-code": {
      color: theme.mode === "light" ? "#6b7280" : "#9ca3af",
      whiteSpace: "nowrap",
    },
  },
  phoneInputError: {
    "& .react-tel-input .form-control": {
      boxShadow: "inset 0 0 0 1.5px rgba(239, 68, 68, 0.7) !important",
    },
  },

  submitBtn: {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 500,
    color: "#ffffff",
    background: "linear-gradient(to right, #374151, #4b5563)",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    boxShadow:
      "inset 0 -2px 25px -4px rgba(255,255,255,0.2), inset 0 0 0 1px rgba(255,255,255,0.10)",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
    marginTop: 8,
    "&:hover": {
      background: "linear-gradient(to right, #4b5563, #6b7280)",
      boxShadow:
        "inset 0 -2px 25px -4px rgba(255,255,255,0.2), inset 0 0 0 1px rgba(209,213,219,0.4), 0 8px 24px -8px rgba(0,0,0,0.5)",
    },
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
    "&:focus": { outline: "none" },
  },

  footerRow: {
    marginTop: 16,
    fontSize: 12,
    color: theme.mode === "light" ? "#4b5563" : "#6b7280",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
  },
  loginLink: {
    color: theme.mode === "light" ? "#1f2937" : "#d1d5db",
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": {
      color: theme.mode === "light" ? "#111827" : "#ffffff",
      textDecoration: "underline",
      textUnderlineOffset: 4,
    },
  },
  whatsappButton: {
    position: "fixed",
    right: 20,
    bottom: 20,
    zIndex: 1200,
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    borderRadius: 999,
    color: "#ffffff",
    textDecoration: "none",
    background: "linear-gradient(135deg, #25D366 0%, #1FA851 100%)",
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
    fontSize: 13,
    fontWeight: 600,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      color: "#ffffff",
      textDecoration: "none",
      transform: "scale(1.02)",
      boxShadow: "0 14px 36px rgba(0,0,0,0.38)",
    },
    "&:active": {
      transform: "scale(0.98)",
    },
    [theme.breakpoints.down("xs")]: {
      right: 16,
      bottom: 16,
      padding: "12px 14px",
    },
  },
  whatsappIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 20,
    height: 20,
    flexShrink: 0,
  },
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("validation.tooShort"))
    .max(50, i18n.t("validation.tooLong"))
    .required(i18n.t("validation.required")),
  companyName: Yup.string()
    .min(2, i18n.t("validation.tooShort"))
    .max(50, i18n.t("validation.tooLong"))
    .required(i18n.t("validation.required")),
  password: Yup.string().min(5, i18n.t("validation.tooShort")).max(50, i18n.t("validation.tooLong")),
  email: Yup.string().email(i18n.t("validation.invalidEmail")).required(i18n.t("validation.required")),
  phone: Yup.string().required(i18n.t("validation.required")),
});

const SignUp = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { colorMode } = useContext(ColorModeContext);
  const { handleLogin } = useContext(AuthContext);
  const { getPlanList } = usePlans();
  const { getPublicSetting } = useSettings();
  const [plans, setPlans] = useState([]);
  const [, setLoading] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [whatsappButtonEnabled, setWhatsappButtonEnabled] = useState(true);
  const [whatsappButtonPhone, setWhatsappButtonPhone] = useState(WHATSAPP_PHONE);

  const languageOptions = [
    { code: "es", shortCode: "es", flag: "/flags/es.png", name: "Español" },
    { code: "pt-BR", shortCode: "pt", flag: "/flags/br.png", name: "Português" },
    { code: "en", shortCode: "en", flag: "/flags/us.png", name: "English" },
    { code: "tr", shortCode: "tr", flag: "/flags/tr.png", name: "Türkçe" },
    { code: "ar", shortCode: "ar", flag: "/flags/sa.png", name: "العربية" },
  ];

  const currentLanguage = localStorage.getItem("i18nextLng") || "es";
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState(
    getSignupPhoneCountry(currentLanguage)
  );
  const selectedLanguage = languageOptions.find(
    lang => lang.code === currentLanguage || lang.shortCode === currentLanguage
  ) || languageOptions[0];

  const isLangSelected = (lang) =>
    lang.code === currentLanguage || lang.shortCode === currentLanguage;

  const langHeaderLabel = (() => {
    const l = (currentLanguage || "es").split("-")[0];
    return l === "en" ? "Language" : l === "es" ? "Idioma" : l === "tr" ? "Dil" : l === "ar" ? "اللغة" : "Idioma";
  })();

  const whatsappLanguage = normalizeSignupLanguage(currentLanguage) || "pt";
  const whatsappText = WHATSAPP_TEXTS[whatsappLanguage] || WHATSAPP_TEXTS.pt;
  const normalizedWhatsAppPhone =
    normalizeWhatsAppPhone(whatsappButtonPhone) || WHATSAPP_PHONE;

  let companyId = null;
  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    planId: "",
  };

  const [user] = useState(initialState);

  const backendUrl =
    BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : BACKEND_URL;

  const getLogoPath = () => {
    const isDark = theme.mode === "dark";
    if (isDark && typeof theme.calculatedLoginLogoDark === "function") {
      return theme.calculatedLoginLogoDark();
    }
    if (!isDark && typeof theme.calculatedLoginLogoLight === "function") {
      return theme.calculatedLoginLogoLight();
    }
    return isDark
      ? colorMode.appLogoLoginDark || colorMode.appLogoDark || "/logo-dark.png"
      : colorMode.appLogoLoginLight || colorMode.appLogoLight || "/logo-light.png";
  };

  // Força re-render quando o idioma muda
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const onLangChanged = () => forceUpdate((n) => n + 1);
    i18n.on("languageChanged", onLangChanged);
    return () => i18n.off("languageChanged", onLangChanged);
  }, []);

  // Suporte a URLs publicas por idioma — forca o idioma a partir da URL
  const { lang: langParam } = useParams();
  useEffect(() => {
    const languageFromUrl = getSignupLanguageFromPath(location.pathname, langParam);
    if (!languageFromUrl) return;
    try {
      i18n.changeLanguage(languageFromUrl);
      localStorage.setItem("i18nextLng", languageFromUrl);
    } catch {}
  }, [langParam, location.pathname]);

  // Sem :lang na URL → aplica o idioma padrão da empresa (público)
  useEffect(() => {
    if (getSignupLanguageFromPath(location.pathname, langParam)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await openApi.get(
          "/companySettings/public/languages"
        );
        if (cancelled) return;
        let savedLang = null;
        try {
          savedLang = localStorage.getItem("i18nextLng");
        } catch {}
        if (!savedLang && data?.defaultLanguage) {
          const short = data.defaultLanguage.split("-")[0];
          i18n.changeLanguage(short);
          try {
            localStorage.setItem("i18nextLng", data.defaultLanguage);
          } catch {}
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [langParam, location.pathname]);

  useEffect(() => {
    document.body.classList.add("signup-page");
    return () => {
      document.body.classList.remove("signup-page");
    };
  }, []);

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch user creation status");
        const data = await response.json();
        const isEnabled = data.userCreation === "enabled";
        setUserCreationEnabled(isEnabled);
        if (!isEnabled) {
          toast.info(i18n.t("signup.toasts.userCreationDisabled"));
          history.push("/login");
        }
      } catch (err) {
        console.error("Erro ao verificar userCreation:", err);
        setUserCreationEnabled(false);
        toast.error(i18n.t("signup.toasts.verificationError"));
        history.push("/login");
      }
    };
    fetchUserCreationStatus();
  }, [backendUrl, history]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const planList = await getPlanList({ listPublic: "false" });
      setPlans(planList);
      if (planList && planList.length > 0) {
        user.planId = planList[0].id;
      }
      setLoading(false);
    };
    fetchData();
  }, [getPlanList]);

  useEffect(() => {
    let cancelled = false;
    const fetchWhatsAppSettings = async () => {
      try {
        const [enabled, phone] = await Promise.all([
          getPublicSetting("whatsappTrialButtonEnabled", companyId),
          getPublicSetting("whatsappTrialButtonPhone", companyId),
        ]);
        if (cancelled) return;
        setWhatsappButtonEnabled(enabled ? enabled === "enabled" : true);
        setWhatsappButtonPhone(normalizeWhatsAppPhone(phone) || WHATSAPP_PHONE);
      } catch {
        if (!cancelled) {
          setWhatsappButtonEnabled(true);
          setWhatsappButtonPhone(WHATSAPP_PHONE);
        }
      }
    };
    fetchWhatsAppSettings();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignUp = async (values) => {
    try {
      const lang = (localStorage.getItem("i18nextLng") || "es").split("-")[0];
      const submitData = {
        ...values,
        planId: values.planId || (plans.length > 0 ? plans[0].id : 1),
        language: lang,
      };
      await openApi.post("/auth/signup", submitData);
      toast.success(i18n.t("signup.toasts.success"));
      await handleLogin({ email: values.email, password: values.password });
    } catch (err) {
      toastError(err);
    }
  };

  const handleLanguageOpen = (event) => setLanguageMenuAnchor(event.currentTarget);
  const handleLanguageClose = () => setLanguageMenuAnchor(null);
  const handleLanguageChange = (languageCode) => {
    const shortCode = normalizeSignupLanguage(languageCode) || "pt";
    i18n.changeLanguage(shortCode);
    localStorage.setItem("i18nextLng", shortCode);
    handleLanguageClose();
    history.replace(`${SIGNUP_LANGUAGE_PATHS[shortCode] || "/signup"}${window.location.search}`);
  };

  if (!userCreationEnabled) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{i18n.t("signup.title")}</title>
      </Helmet>

      <div className={classes.root}>
        {whatsappButtonEnabled && normalizedWhatsAppPhone && (
          <a
            href={getConfigurableWhatsAppUrl(
              normalizedWhatsAppPhone,
              whatsappText.message
            )}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.whatsappButton}
            aria-label="Abrir WhatsApp"
          >
            <span className={classes.whatsappIcon}>
              <WhatsAppIcon />
            </span>
            <span>{whatsappText.label}</span>
          </a>
        )}

        <div className={classes.topRightControls}>
          <div className={classes.langTriggerButton} onClick={handleLanguageOpen}>
            <img
              src={selectedLanguage.flag}
              alt={selectedLanguage.name}
              className={classes.langTriggerFlag}
            />
          </div>
          <ThemeToggle scale={0.75} />
        </div>

        <Popover
          open={Boolean(languageMenuAnchor)}
          anchorEl={languageMenuAnchor}
          onClose={handleLanguageClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          PaperProps={{ className: classes.langPopoverPaper }}
          disableScrollLock
        >
          <div className={classes.langHeader}>
            <Typography className={classes.langHeaderTitle}>
              {langHeaderLabel}
            </Typography>
          </div>
          <div className={classes.langOptionsList}>
            {languageOptions.map((lang) => {
              const selected = isLangSelected(lang);
              return (
                <div
                  key={lang.code}
                  className={`${classes.langOptionItem} ${selected ? classes.langOptionItemSelected : ""}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <img
                    src={lang.flag}
                    alt={lang.name}
                    className={classes.langFlagImg}
                  />
                  <Typography className={classes.langOptionName}>
                    {lang.name}
                  </Typography>
                  <div className={classes.langCheckWrapper}>
                    {selected && <CheckIcon />}
                  </div>
                </div>
              );
            })}
          </div>
        </Popover>

        <div className={classes.cardWrap}>
          <div className={classes.blurLayer}>
            <div className={classes.blurOrb1} />
            <div className={classes.blurOrb2} />
          </div>
          <div className={classes.borderGradient} />

          <div className={classes.card}>
            <div className={classes.cardInner}>
              <img
                src={getLogoPath()}
                alt={i18n.t("login.logoAlt")}
                className={classes.logoImg}
              />

              <div className={classes.header}>
                <div className={classes.secureRow}>
                  <span className={classes.secureDot} />
                  <span>{i18n.t("login.secureArea")}</span>
                </div>
                <h2 className={classes.title}>{i18n.t("signup.title")}</h2>
              </div>

              <Formik
                initialValues={user}
                enableReinitialize={true}
                validationSchema={UserSchema}
                onSubmit={(values, actions) => {
                  setTimeout(() => {
                    handleSignUp(values);
                    actions.setSubmitting(false);
                  }, 400);
                }}
              >
                {({ values, touched, errors, isSubmitting, setFieldValue, setFieldTouched }) => (
                  <Form className={classes.form}>
                    {/* Company */}
                    <div>
                      <label htmlFor="companyName" className={classes.fieldLabel}>
                        {i18n.t("signup.form.company")}
                      </label>
                      <div className={classes.inputWrap}>
                        <span className={classes.inputIconLeft}>
                          <Building2 size={16} strokeWidth={1.5} />
                        </span>
                        <Field
                          id="companyName"
                          name="companyName"
                          autoComplete="organization"
                          autoFocus
                          className={`${classes.input} ${touched.companyName && errors.companyName ? classes.inputError : ""}`}
                          placeholder={i18n.t("signup.form.company")}
                        />
                      </div>
                      {touched.companyName && errors.companyName && (
                        <div className={classes.helperError}>{errors.companyName}</div>
                      )}
                    </div>

                    {/* Name */}
                    <div>
                      <label htmlFor="name" className={classes.fieldLabel}>
                        {i18n.t("signup.form.name")}
                      </label>
                      <div className={classes.inputWrap}>
                        <span className={classes.inputIconLeft}>
                          <User size={16} strokeWidth={1.5} />
                        </span>
                        <Field
                          id="name"
                          name="name"
                          autoComplete="name"
                          className={`${classes.input} ${touched.name && errors.name ? classes.inputError : ""}`}
                          placeholder={i18n.t("signup.form.name")}
                        />
                      </div>
                      {touched.name && errors.name && (
                        <div className={classes.helperError}>{errors.name}</div>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className={classes.fieldLabel}>
                        {i18n.t("signup.form.email")}
                      </label>
                      <div className={classes.inputWrap}>
                        <span className={classes.inputIconLeft}>
                          <Mail size={16} strokeWidth={1.5} />
                        </span>
                        <Field
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          className={`${classes.input} ${touched.email && errors.email ? classes.inputError : ""}`}
                          placeholder={i18n.t("login.emailPlaceholder")}
                          style={{ textTransform: "lowercase" }}
                        />
                      </div>
                      {touched.email && errors.email && (
                        <div className={classes.helperError}>{errors.email}</div>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="password" className={classes.fieldLabel}>
                        {i18n.t("signup.form.password")}
                      </label>
                      <div className={classes.inputWrap}>
                        <span className={classes.inputIconLeft}>
                          <Lock size={16} strokeWidth={1.5} />
                        </span>
                        <Field
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          className={`${classes.input} ${classes.inputWithRight} ${touched.password && errors.password ? classes.inputError : ""}`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          className={classes.inputIconRight}
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={i18n.t("login.togglePassword")}
                        >
                          {showPassword ? (
                            <EyeOff size={16} strokeWidth={1.5} />
                          ) : (
                            <Eye size={16} strokeWidth={1.5} />
                          )}
                        </button>
                      </div>
                      {touched.password && errors.password && (
                        <div className={classes.helperError}>{errors.password}</div>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="phone" className={classes.fieldLabel}>
                        {i18n.t("signup.form.phone")}
                      </label>
                      <div
                        className={`${classes.phoneInputWrap} ${touched.phone && errors.phone ? classes.phoneInputError : ""}`}
                      >
                        <PhoneInput
                          country={selectedPhoneCountry}
                          value={values.phone}
                          onChange={(phone, countryData) => {
                            if (countryData?.countryCode) {
                              setSelectedPhoneCountry(countryData.countryCode);
                            }
                            setFieldValue("phone", phone);
                          }}
                          onBlur={() => setFieldTouched("phone", true)}
                          placeholder={i18n.t("signup.form.phonePlaceholder")}
                          inputProps={{
                            id: "phone",
                            name: "phone",
                            required: true,
                            autoComplete: "tel",
                          }}
                          enableSearch
                          searchPlaceholder={SIGNUP_PHONE_SEARCH_PLACEHOLDER[normalizeSignupLanguage(currentLanguage) || "pt"]}
                          countryCodeEditable={false}
                          specialLabel=""
                        />
                      </div>
                      {touched.phone && errors.phone && (
                        <div className={classes.helperError}>{errors.phone}</div>
                      )}
                    </div>

                    <button
                      type="submit"
                      className={classes.submitBtn}
                      disabled={isSubmitting}
                    >
                      <UserPlus size={16} strokeWidth={2} />
                      {i18n.t("signup.buttons.submit")}
                    </button>

                    <div className={classes.footerRow}>
                      <RouterLink to="/login" className={classes.loginLink}>
                        {i18n.t("signup.buttons.login")}
                      </RouterLink>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;
